import React, { useState } from 'react';
import { AuthService } from 'services/AuthService';
import { useAuth } from 'contexts/AuthContext';
import { getConfig } from 'utils/appConfig';

// ============================================================
// CambiarPasswordPage — Cambio de contraseña (forzado o voluntario)
// ============================================================

interface Props {
  forzado?: boolean;
}

interface Regla {
  label: string;
  ok:    (v: string) => boolean;
}

const REGLAS: Regla[] = [
  { label: 'Mínimo 8 caracteres',    ok: v => v.length >= 8 },
  { label: 'Al menos una mayúscula', ok: v => /[A-Z]/.test(v) },
  { label: 'Al menos una minúscula', ok: v => /[a-z]/.test(v) },
  { label: 'Al menos un número',     ok: v => /\d/.test(v) },
];

const CheckIcon = ({ ok }: { ok: boolean }) => (
  <svg viewBox="0 0 16 16" fill="currentColor" className={`w-3.5 h-3.5 flex-shrink-0 ${ok ? 'text-green-500' : 'text-slate-300'}`}>
    {ok
      ? <path fillRule="evenodd" d="M12.416 3.376a.75.75 0 0 1 .208 1.04l-5 7.5a.75.75 0 0 1-1.154.114l-3-3a.75.75 0 0 1 1.06-1.06l2.353 2.353 4.493-6.74a.75.75 0 0 1 1.04-.207Z" clipRule="evenodd"/>
      : <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5" fill="none"/>
    }
  </svg>
);

const EyeToggle = ({ open, onClick }: { open: boolean; onClick: () => void }) => (
  <button type="button" onClick={onClick} tabIndex={-1}
    className="absolute inset-y-0 right-3 flex items-center text-slate-400 hover:text-slate-600">
    <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
      {open
        ? <><path d="M10 12.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z"/><path fillRule="evenodd" d="M.664 10.59a1.651 1.651 0 0 1 0-1.186A10.004 10.004 0 0 1 10 3c4.257 0 7.893 2.66 9.336 6.41.147.381.146.804 0 1.186A10.004 10.004 0 0 1 10 17c-4.257 0-7.893-2.66-9.336-6.41ZM14 10a4 4 0 1 1-8 0 4 4 0 0 1 8 0Z" clipRule="evenodd"/></>
        : <path fillRule="evenodd" d="M3.28 2.22a.75.75 0 0 0-1.06 1.06l14.5 14.5a.75.75 0 1 0 1.06-1.06l-1.745-1.745a10.029 10.029 0 0 0 3.3-4.38 1.651 1.651 0 0 0 0-1.185A10.004 10.004 0 0 0 9.999 3a9.956 9.956 0 0 0-4.744 1.194L3.28 2.22ZM7.752 6.69l1.092 1.092a2.5 2.5 0 0 1 3.374 3.373l1.091 1.092a4 4 0 0 0-5.557-5.557Z" clipRule="evenodd"/>
      }
    </svg>
  </button>
);

const SpinnerIcon = () => (
  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z"/>
  </svg>
);

const CambiarPasswordPage: React.FC<Props> = ({ forzado = false }) => {
  const { clearPasswordExpirada, logout } = useAuth();

  const [actual,      setActual]      = useState('');
  const [nueva,       setNueva]       = useState('');
  const [confirmacion, setConfirmacion] = useState('');
  const [showActual,  setShowActual]  = useState(false);
  const [showNueva,   setShowNueva]   = useState(false);
  const [showConf,    setShowConf]    = useState(false);

  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState<string | null>(null);
  const [success,  setSuccess]  = useState(false);

  const reglasOk = REGLAS.map(r => r.ok(nueva));
  const todasOk  = reglasOk.every(Boolean);
  const coincide = nueva === confirmacion && confirmacion.length > 0;

  const puedeEnviar = actual.trim() && todasOk && coincide && !saving;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!puedeEnviar) return;

    setSaving(true);
    setError(null);

    try {
      await AuthService.changePassword({
        passwordActual:       actual,
        passwordNueva:        nueva,
        passwordConfirmacion: confirmacion,
      });
      setSuccess(true);
      setTimeout(() => clearPasswordExpirada(), 1500);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Error al cambiar la contraseña');
    } finally {
      setSaving(false);
    }
  };

  const inputClass = 'w-full pl-3 pr-10 py-2.5 rounded-lg border border-slate-200 bg-slate-50 text-slate-800 text-sm placeholder-slate-300 outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all';

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100 relative overflow-hidden">
      {/* Fondo decorativo */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-blue-700 opacity-10"/>
        <div className="absolute -bottom-20 -right-20 w-80 h-80 rounded-full bg-blue-900 opacity-10"/>
      </div>

      <div className="relative w-full max-w-md mx-4">
        {/* Cabecera */}
        <div className="bg-blue-900 rounded-t-2xl px-8 py-6 text-center shadow-lg">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-blue-700 ring-4 ring-blue-600/40 mb-3">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="w-6 h-6 text-white">
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z"/>
            </svg>
          </div>
          <h1 className="text-white text-xl font-bold">Cambiar Contraseña</h1>
          {forzado && (
            <p className="text-amber-300 text-xs mt-1 font-medium">
              Tu contraseña ha expirado. Debes actualizarla para continuar.
            </p>
          )}
          {!forzado && (
            <p className="text-blue-300 text-sm mt-1">{getConfig().institucionNombre}</p>
          )}
        </div>

        {/* Formulario */}
        <div className="bg-white rounded-b-2xl px-8 pt-7 pb-8 shadow-xl">
          {success ? (
            <div className="text-center py-6">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-green-100 mb-4">
                <svg viewBox="0 0 20 20" fill="currentColor" className="w-8 h-8 text-green-600">
                  <path fillRule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm3.857-9.809a.75.75 0 0 0-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 1 0-1.06 1.061l2.5 2.5a.75.75 0 0 0 1.137-.089l4-5.5Z" clipRule="evenodd"/>
                </svg>
              </div>
              <p className="text-slate-800 font-semibold text-lg">Contraseña actualizada</p>
              <p className="text-slate-400 text-sm mt-1">Redirigiendo al sistema...</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} noValidate className="space-y-4">

              {/* Contraseña actual */}
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
                  Contraseña actual
                </label>
                <div className="relative">
                  <input
                    type={showActual ? 'text' : 'password'}
                    value={actual}
                    onChange={e => { setActual(e.target.value); setError(null); }}
                    placeholder="Contraseña actual"
                    disabled={saving}
                    className={inputClass}
                    autoComplete="current-password"
                  />
                  <EyeToggle open={showActual} onClick={() => setShowActual(v => !v)}/>
                </div>
              </div>

              {/* Nueva contraseña */}
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
                  Nueva contraseña
                </label>
                <div className="relative">
                  <input
                    type={showNueva ? 'text' : 'password'}
                    value={nueva}
                    onChange={e => { setNueva(e.target.value); setError(null); }}
                    placeholder="Nueva contraseña"
                    disabled={saving}
                    className={inputClass}
                    autoComplete="new-password"
                  />
                  <EyeToggle open={showNueva} onClick={() => setShowNueva(v => !v)}/>
                </div>

                {/* Reglas en tiempo real */}
                {nueva.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {REGLAS.map((r, i) => (
                      <li key={r.label} className="flex items-center gap-1.5">
                        <CheckIcon ok={reglasOk[i]}/>
                        <span className={`text-xs ${reglasOk[i] ? 'text-green-600' : 'text-slate-400'}`}>
                          {r.label}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Confirmar contraseña */}
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
                  Confirmar nueva contraseña
                </label>
                <div className="relative">
                  <input
                    type={showConf ? 'text' : 'password'}
                    value={confirmacion}
                    onChange={e => { setConfirmacion(e.target.value); setError(null); }}
                    placeholder="Repite la nueva contraseña"
                    disabled={saving}
                    className={`${inputClass} ${confirmacion.length > 0 && !coincide ? 'border-red-400 bg-red-50 focus:ring-red-400' : ''}`}
                    autoComplete="new-password"
                  />
                  <EyeToggle open={showConf} onClick={() => setShowConf(v => !v)}/>
                </div>
                {confirmacion.length > 0 && !coincide && (
                  <p className="text-red-500 text-xs mt-1">Las contraseñas no coinciden</p>
                )}
              </div>

              {/* Error del servidor */}
              {error && (
                <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
                  <svg className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0Zm-8-5a.75.75 0 0 1 .75.75v4.5a.75.75 0 0 1-1.5 0v-4.5A.75.75 0 0 1 10 5Zm0 10a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z" clipRule="evenodd"/>
                  </svg>
                  <span className="text-red-600 text-sm">{error}</span>
                </div>
              )}

              {/* Botones */}
              <div className={`flex gap-3 pt-1 ${forzado ? 'justify-end' : 'justify-between'}`}>
                {!forzado && (
                  <button
                    type="button"
                    onClick={() => clearPasswordExpirada()}
                    className="px-4 py-2.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 text-sm font-semibold transition-colors"
                  >
                    Cancelar
                  </button>
                )}
                {forzado && (
                  <button
                    type="button"
                    onClick={() => logout()}
                    className="px-4 py-2.5 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 text-sm font-semibold transition-colors"
                  >
                    Cerrar sesión
                  </button>
                )}
                <button
                  type="submit"
                  disabled={!puedeEnviar}
                  className="flex items-center gap-2 px-6 py-2.5 rounded-lg bg-blue-700 hover:bg-blue-800 text-white text-sm font-bold transition-colors shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {saving && <SpinnerIcon/>}
                  Cambiar contraseña
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default CambiarPasswordPage;
